import { createModel } from "@rematch/core";
import { api } from "api";
import { ExcelColumn, FailureTypeVm } from "api/generated/lumen";
import { RootModel } from "app/store";
import { mapToGetAllQuery } from "utils";
import { downloadFile } from "utils/file";
import {
  ListRequestParams,
  mergeListParamsWithPagination,
  OrderByType,
} from "widgets/table/useTableUtils";
import { getFilterParamsForFailureTypesGetRequests } from "../utils";

interface FailureTypesListState {
  listParams: ListRequestParams;
  list: FailureTypeVm[];
}

const initialState: FailureTypesListState = {
  listParams: {
    pageSize: 10,
    order: [
      {
        orderBy: "name",
        orderByType: OrderByType.Ascending,
      },
    ],
  },
  list: [],
};

export const failureTypesList = createModel<RootModel>()({
  state: initialState,
  reducers: {
    resetListParams(state) {
      state.listParams = {
        ...initialState.listParams,
        order: initialState.listParams.order,
      };
    },
    setList(
      state,
      {
        data,
        listParams,
      }: { data: FailureTypeVm[]; listParams: ListRequestParams }
    ) {
      state.list = data;
      state.listParams = listParams;
    },
    reset: () => initialState,
  },
  effects: (dispatch) => {
    const { failureTypesList } = dispatch;

    return {
      async fetchList(params: ListRequestParams = {}, state): Promise<void> {
        const { mergedParams, payload } =
          getFilterParamsForFailureTypesGetRequests(
            params,
            state.failureTypesList.listParams
          );

        const data = await api.lumen.failureTypes.apiFailureTypesGetAllPost({
          getAllFailureTypesQuery: mapToGetAllQuery(payload),
        });

        const { items, ...pagination } = data;

        dispatch(
          failureTypesList.setList({
            data: items || [],
            listParams: mergeListParamsWithPagination(mergedParams, pagination),
          })
        );
      },

      async deleteItem(
        {
          id,
        }: {
          id?: string;
        },
        state
      ): Promise<void> {
        if (typeof id === "string") {
          return api.lumen.failureTypes.apiFailureTypesIdDelete({ id });
        }
      },

      async generateExcel(columns: ExcelColumn[], state): Promise<void> {
        const { payload } = getFilterParamsForFailureTypesGetRequests(
          state.failureTypesList.listParams,
          state.failureTypesList.listParams
        );

        const data = await api.lumen.failureTypes.apiFailureTypesExportPostRaw({
          getAllFailureTypesExcelQuery: {
            ...mapToGetAllQuery(payload),
            columns,
            pageSize: -1,
          },
        });

        downloadFile(data.raw);
      },
    };
  },
});
